import { useMemo } from "react";
import { mediaIdsFromHtml } from "#app/lib/media-content";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";
import type { IssueAttachment, IssueFieldMedia } from "#app/lib/data";
import type { FormPhotosOnFile } from "#app/lib/forms";
import type { Omittable } from "@salestrak/ui";

/** One piece of media somewhere on the issue, tagged with where it lives.
 * `contentType` drives lightbox rendering; field/inline media are always
 * images, note footer media carries its real type. */
export type IssueMediaItem = (
  | { photoId: string; source: "upload"; upload: IssueAttachment }
  | {
      photoId: string;
      source: "field";
      field: CustomFieldDef;
      /** Undefined until the server has said (a photo added this session). */
      uploadedBy?: string | null | undefined;
    }
  | { photoId: string; source: "form"; onFile: FormPhotosOnFile }
  | { photoId: string; source: "note" | "note_inline"; note: NoteNode }
) &
  // `fileName` is read straight off a nullable media field, and every consumer
  // spells the miss `?? …` — an absent key and an explicit `undefined` are the
  // same answer, so the member relaxes. `name` is the one a member gave it
  // (inline note images have no media record, so never carry one).
  Omittable<{ contentType: string; fileName?: string | null; name?: string | null }>;

const NO_UPLOADS: readonly IssueAttachment[] = [];
const NO_FIELD_MEDIA: readonly IssueFieldMedia[] = [];
const NO_FORM_PHOTOS: readonly FormPhotosOnFile[] = [];

/**
 * Aggregates every photo attached to the issue for the Media tab: uploads
 * first, then field photos (matching the summary tab's order), form photos,
 * and notes in feed order.
 */
export function useIssueMediaItems(
  customFields: CustomFieldDef[],
  values: Record<string, unknown> | null,
  notes: NoteNode[],
  uploads: readonly IssueAttachment[] = NO_UPLOADS,
  fieldMedia: readonly IssueFieldMedia[] = NO_FIELD_MEDIA,
  formPhotos: readonly FormPhotosOnFile[] = NO_FORM_PHOTOS,
): IssueMediaItem[] {
  return useMemo(() => {
    const items: IssueMediaItem[] = uploads.map((upload) => ({
      photoId: upload.id,
      source: "upload",
      upload,
      contentType: upload.contentType,
      fileName: upload.fileName,
      name: upload.name,
    }));

    const records = new Map(fieldMedia.map((media) => [media.id, media]));
    for (const field of customFields) {
      if (field.type !== "photos") continue;
      const value = values?.[field.customFieldId];
      if (!Array.isArray(value)) continue;
      for (const photoId of value) {
        if (typeof photoId === "string") {
          items.push({
            photoId,
            source: "field",
            field,
            uploadedBy: records.get(photoId)?.createdBy,
            name: records.get(photoId)?.name,
            contentType: "image/*",
          });
        }
      }
    }

    for (const onFile of formPhotos) {
      const names = new Map(onFile.submission.media.map((media) => [media.id, media.name]));
      for (const photoId of onFile.photoIds) {
        items.push({ photoId, source: "form", onFile, contentType: "image/*", name: names.get(photoId) ?? null });
      }
    }

    const walk = (note: NoteNode) => {
      for (const photoId of mediaIdsFromHtml(note.note ?? "")) {
        items.push({ photoId, source: "note_inline", note, contentType: "image/*" });
      }
      for (const media of note.media ?? []) {
        items.push({
          photoId: media.id,
          source: "note",
          note,
          contentType: media.contentType,
          fileName: media.fileName,
          name: media.name,
        });
      }
      note.replies?.forEach(walk);
    };
    notes.forEach(walk);
    const seen = new Set<string>();
    return items.filter((item) => {
      const key = `${item.source}:${item.photoId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [customFields, fieldMedia, formPhotos, notes, uploads, values]);
}
